import { useState, useEffect } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Camera, Check, AlertTriangle, Loader2, FileSignature } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";
import BrandingFooter from "../components/BrandingFooter";
import FloatingBrandingBadge from "../components/FloatingBrandingBadge";

const ContractSignPage = () => {
  const { contractId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);
  
  const [signature, setSignature] = useState({
    typed_name: "",
    agreed_to_terms: false
  });

  useEffect(() => {
    if (contractId && token) {
      fetchContract();
    } else {
      setError("Invalid contract link");
      setLoading(false);
    }
  }, [contractId, token]);

  const fetchContract = async () => {
    try {
      const response = await axios.get(`${API}/contracts/sign/${contractId}?token=${token}`);
      setContract(response.data);
      if (response.data.already_signed) {
        setSigned(true);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Contract not found");
    } finally {
      setLoading(false);
    }
  };

  const handleSign = async () => {
    if (!signature.typed_name.trim()) {
      toast.error("Please enter your full name");
      return;
    }
    if (!signature.agreed_to_terms) {
      toast.error("You must agree to the terms");
      return;
    }

    setSigning(true);
    try {
      const response = await axios.post(
        `${API}/contracts/sign/${contractId}?token=${token}`,
        signature
      );
      setSigned(true);
      toast.success("Contract signed successfully!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to sign contract");
    } finally {
      setSigning(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-card border-border">
          <CardContent className="py-12 text-center">
            <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-red-500" />
            <h2 className="text-xl font-medium mb-2">Unable to Load Contract</h2>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (signed || contract?.already_signed) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-card border-border">
          <CardContent className="py-12 text-center">
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-green-500" />
            </div>
            <h2 className="text-xl font-medium mb-2">Contract Signed</h2>
            <p className="text-muted-foreground mb-4">
              {contract?.already_signed 
                ? "This contract has already been signed."
                : "Thank you for signing the contract."}
            </p>
            {contract?.signature && (
              <div className="text-sm text-muted-foreground">
                <p>Signed by: {contract.signature.typed_name}</p>
                <p>Date: {new Date(contract.signature.signed_at).toLocaleString()}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="contract-sign-page">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Camera className="w-6 h-6 text-primary" />
            <span className="font-serif text-lg">360Lumina</span>
          </div>
          {contract?.photographer && (
            <div className="text-sm text-muted-foreground">
              From: {contract.photographer.business_name || contract.photographer.display_name}
            </div>
          )}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Contract Info */}
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-serif mb-2">{contract?.template_name}</h1>
          <p className="text-muted-foreground">
            Please review the contract below and sign to agree to the terms.
          </p>
        </div>

        {/* Contract Content */}
        <Card className="bg-card border-border mb-8">
          <CardContent className="p-6 md:p-8">
            <div 
              className="prose prose-invert max-w-none"
              dangerouslySetInnerHTML={{ __html: contract?.content }}
            />
          </CardContent>
        </Card>

        {/* Signature Section */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileSignature className="w-5 h-5 text-primary" />
              Sign Contract
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <Label htmlFor="signature">Type your full legal name</Label>
              <Input
                id="signature"
                value={signature.typed_name}
                onChange={(e) => setSignature(prev => ({ ...prev, typed_name: e.target.value }))}
                placeholder="Your Full Name"
                className="text-lg"
                data-testid="signature-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                By typing your name, you are creating a legally binding electronic signature.
              </p>
            </div>

            <div className="flex items-start gap-3">
              <Checkbox
                id="agree"
                checked={signature.agreed_to_terms}
                onCheckedChange={(checked) => setSignature(prev => ({ ...prev, agreed_to_terms: checked }))}
                data-testid="agree-checkbox"
              />
              <Label htmlFor="agree" className="text-sm leading-relaxed cursor-pointer">
                I have read and agree to the terms and conditions outlined in this contract. 
                I understand that by typing my name and clicking &quot;Sign Contract&quot;, I am creating 
                a legally binding electronic signature.
              </Label>
            </div>

            <Button 
              onClick={handleSign} 
              disabled={signing || !signature.typed_name || !signature.agreed_to_terms}
              className="w-full"
              size="lg"
              data-testid="sign-contract-btn"
            >
              {signing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Signing...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Sign Contract
                </>
              )}
            </Button>

            <p className="text-xs text-center text-muted-foreground">
              Your signature will be timestamped and your IP address will be recorded for verification purposes.
            </p>
          </CardContent>
        </Card>
      </main>

      {/* Footer */}
      <footer className="border-t border-border mt-12 py-6 text-center text-sm text-muted-foreground">
        <p className="mb-2 flex items-center justify-center gap-1">
          <BrandingFooter planKey={contract?.photographer_plan_key} />
          {contract?.photographer_plan_key !== "studio" && contract?.photographer_plan_key !== "legacy" && " • "}
          Secure E-Signature
        </p>
        <div className="flex items-center justify-center gap-4">
          <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
          <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
        </div>
      </footer>

      {/* Floating branding badge for Starter/Pro tiers */}
      <FloatingBrandingBadge planKey={contract?.photographer_plan_key} />
    </div>
  );
};

export default ContractSignPage;
